import { z } from 'zod';

import type { DocumentList } from '#domain/Document.ts';
import { apiV2Root } from '#store/apiV2Root.ts';

import { mapDocumentList } from './adapters/documentAdapter.ts';
import { type ApiWriteDocument, apiSchema } from './model/types.ts';
import { validateWarn } from './validateZodHelper.ts';

const TAG = 'DOCUMENTS';
const ordersUrl = '/orders';
const toursUrl = '/tours';
const stopsUrl = '/stops';

export const documentApiSlice = apiV2Root.injectEndpoints({
    endpoints: build => ({
        getOrderDocuments: build.query<DocumentList, { orderId: string }>({
            query: ({ orderId }) => `${ordersUrl}/${orderId}/documents`,
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapDocumentList(validateWarn(apiSchema.DocumentList)(response, meta, arg)),
        }),
        getTourDocuments: build.query<DocumentList, { tourId: string }>({
            query: ({ tourId }) => `${toursUrl}/${tourId}/documents`,
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapDocumentList(validateWarn(apiSchema.DocumentList)(response, meta, arg)),
        }),
        getStopDocuments: build.query<DocumentList, { stopId: string }>({
            query: ({ stopId }) => `${stopsUrl}/${stopId}/documents`,
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapDocumentList(validateWarn(apiSchema.DocumentList)(response, meta, arg)),
        }),
        uploadOrderDocument: build.mutation<null, { orderId: string; document: ApiWriteDocument }>({
            query: ({ orderId, document }) => ({
                url: `${ordersUrl}/${orderId}/documents`,
                method: 'POST',
                body: JSON.stringify(document),
            }),
            invalidatesTags: [TAG],
            transformResponse: validateWarn(z.null()),
        }),
        uploadStopDocument: build.mutation<null, { stopId: string; document: ApiWriteDocument }>({
            query: ({ stopId, document }) => ({
                url: `${stopsUrl}/${stopId}/documents`,
                method: 'POST',
                body: JSON.stringify(document),
            }),
            invalidatesTags: [TAG],
            transformResponse: validateWarn(z.null()),
        }),
        uploadTourDocument: build.mutation<null, { tourId: string; document: ApiWriteDocument }>({
            query: ({ tourId, document }) => ({
                url: `${toursUrl}/${tourId}/documents`,
                method: 'POST',
                body: JSON.stringify(document),
            }),
            invalidatesTags: [TAG],
            transformResponse: validateWarn(z.null()),
        }),
    }),
});

export const {
    useGetOrderDocumentsQuery,
    useGetStopDocumentsQuery,
    useGetTourDocumentsQuery,
    useUploadOrderDocumentMutation,
    useUploadTourDocumentMutation,
    useUploadStopDocumentMutation,
} = documentApiSlice;
